import React, { FC, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Theme } from "@material-ui/core/styles";
import { styled } from "@material-ui/styles";
import { useDispatch, useSelector } from "react-redux";

import CheckBox from "../../../../components/elements/CheckBox/CheckBox";
import CreativeAssets from "../../../CampaignCreationBasicSlides/AdGroupSelectionSlide/components/CreativeAssets/CreativeAssets";
import api from "../../../../api";
import { AdGroupTemplate, AdGroupType, CreativeTemplateAsset } from "../../../../types/adGroupTypes";
import { AdvertiserType } from "../../../../types/advertiserTypes";
import { CreativeAssetFromTemplate, CreativeAssetToCreate } from "../../../../types/creativeTypes";
import { ReduxApplicationState } from "../../../../types/ReduxTypes/ReduxApplicationState";
import {
  getAdGroupTemplates,
  setAdGroupToCreate,
  setSelectedAdGroupTemplate,
} from "../../../../redux/reducers/campaignToCreate";
import { truncateText } from "../../../../helpers/truncateText";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type SavedAdGroupsTabProps = {
  selectedAdvertiser: AdvertiserType | null;
  isVideoCampaign: boolean;
  onSelectionChange?: (selectedId: string | null) => void;
  panelMode?: boolean;
};

type CreativesMap = Record<string, CreativeTemplateAsset[]>;

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const Card = styled("div")(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.white,
  border: `1px solid ${theme.palette.brand.gray200}`,
  borderRadius: 10,
  overflowX: "auto",
}));

const TableEl = styled("table")({
  width: "100%",
  borderCollapse: "collapse",
});

const Thead = styled("thead")(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.gray50,
}));

const Th = styled("th")(({ theme }: { theme: Theme }) => ({
  padding: "10px 16px",
  textAlign: "left" as const,
  fontSize: 10,
  fontWeight: 600,
  textTransform: "uppercase" as const,
  letterSpacing: "0.08em",
  color: theme.palette.brand.gray500,
  borderBottom: `1px solid ${theme.palette.brand.gray200}`,
}));

const Tr = styled("tr")(({ theme }: { theme: Theme }) => ({
  height: 48,
  borderBottom: `1px solid ${theme.palette.brand.gray200}`,
  cursor: "pointer",
  transition: "background 0.1s",
  "&:hover": {
    background: theme.palette.brand.gray50,
  },
  "&:last-child": {
    borderBottom: "none",
  },
}));

const Td = styled("td")(({ theme }: { theme: Theme }) => ({
  padding: "0 16px",
  fontSize: 13,
  color: theme.palette.brand.gray900,
  verticalAlign: "middle" as const,
}));

const ExpandIcon = styled("span")(({ theme }: { theme: Theme }) => ({
  display: "inline-block",
  fontSize: 10,
  color: theme.palette.brand.gray400,
  marginRight: 8,
  transition: "transform 0.2s",
  lineHeight: 1,
  "&[data-open='true']": {
    transform: "rotate(90deg)",
  },
}));

const ExpandedTr = styled("tr")(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.gray50,
  borderBottom: `1px solid ${theme.palette.brand.gray200}`,
}));

const ExpandedTd = styled("td")(({ theme }: { theme: Theme }) => ({
  padding: "12px 20px 16px 40px",
  fontSize: 12,
  color: theme.palette.brand.gray500,
}));

const EmptyState = styled("div")(({ theme }: { theme: Theme }) => ({
  padding: "48px 24px",
  textAlign: "center" as const,
  color: theme.palette.brand.gray500,
  fontSize: 14,
  "& a": {
    color: theme.palette.brand.coral,
    textDecoration: "none",
    fontWeight: 500,
    "&:hover": { textDecoration: "underline" },
  },
}));

const LoadingState = styled("div")(({ theme }: { theme: Theme }) => ({
  padding: "32px 24px",
  textAlign: "center" as const,
  color: theme.palette.brand.gray500,
  fontSize: 14,
}));

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const SavedAdGroupsTab: FC<SavedAdGroupsTabProps> = ({
  selectedAdvertiser,
  isVideoCampaign,
  onSelectionChange,
  panelMode,
}) => {
  const dispatch = useDispatch();
  const adGroupTemplates = useSelector((state: ReduxApplicationState) => state.campaignToCreate.adGroupTemplates);
  const isPending = useSelector((state: ReduxApplicationState) => state.campaignToCreate.isPending);
  const preSelectedAdGroupId = useSelector(
    (state: ReduxApplicationState) => state.campaignToCreate.selectedAdGroupTemplate,
  );

  const hasAutoSelected = useRef(false);
  const hasUserSelectedAdGroup = useRef(false);

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [creativesMap, setCreativesMap] = useState<CreativesMap>({});
  const [fetchingIds, setFetchingIds] = useState<Set<string>>(new Set());
  const [selectedAdGroupId, setSelectedAdGroupId] = useState<string | null>(null);
  const [selectedAdGroup, setSelectedAdGroup] =
    useState<{
      adGroup: AdGroupTemplate;
      creatives: CreativeAssetToCreate[];
    } | null>(null);

  const advertiserId = selectedAdvertiser?.brandgenAdvertiserId;

  // Re-fetch whenever the selected advertiser changes
  useEffect(() => {
    if (advertiserId) {
      dispatch(getAdGroupTemplates(advertiserId));
    }
  }, [dispatch, advertiserId]);

  // Dispatch selection to Redux immediately when it changes (panel mode)
  useEffect(() => {
    if (selectedAdGroup) {
      hasUserSelectedAdGroup.current = true;
      dispatch(
        setAdGroupToCreate({
          name: selectedAdGroup.adGroup.name,
          description: selectedAdGroup.adGroup.description,
          adGroupType: selectedAdGroup.adGroup.adGroupType,
          creativeAssets: selectedAdGroup.creatives,
        }),
      );
      dispatch(setSelectedAdGroupTemplate(selectedAdGroup.adGroup.id));
    } else if (hasUserSelectedAdGroup.current) {
      dispatch(
        setAdGroupToCreate({
          name: "",
          description: "",
          adGroupType: AdGroupType.IMAGE,
          creativeAssets: [],
        }),
      );
      dispatch(setSelectedAdGroupTemplate(null));
    }
  }, [dispatch, selectedAdGroup]);

  useEffect(() => {
    onSelectionChange?.(selectedAdGroupId);
  }, [selectedAdGroupId, onSelectionChange]);

  const filteredTemplates: AdGroupTemplate[] = adGroupTemplates
    .filter((t) => (isVideoCampaign ? t.adGroupType === AdGroupType.VIDEO : t.adGroupType === AdGroupType.IMAGE))
    .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));

  const fetchCreatives = async (templateId: string): Promise<CreativeTemplateAsset[]> => {
    if (creativesMap[templateId]) return creativesMap[templateId];
    setFetchingIds((prev) => new Set(prev).add(templateId));
    try {
      const { payload } = await api.getCreativesTemplatesByAdGroup(templateId);
      const result = payload ?? [];
      setCreativesMap((prev) => ({ ...prev, [templateId]: result }));
      return result;
    } catch (error) {
      console.error("SavedAdGroupsTab: failed to fetch creatives", error);
      setCreativesMap((prev) => ({ ...prev, [templateId]: [] }));
      return [];
    } finally {
      setFetchingIds((prev) => {
        const s = new Set(prev);
        s.delete(templateId);
        return s;
      });
    }
  };

  const handleCheckboxChange = async (templateId: string, currentlySelected: boolean) => {
    if (currentlySelected) {
      setSelectedAdGroupId(null);
      setSelectedAdGroup(null);
      return;
    }

    const creatives = await fetchCreatives(templateId);
    const adGroupToSet = filteredTemplates.find((t) => t.id === templateId)!;
    const creativeAssets: CreativeAssetToCreate[] = creatives.map((c) => {
      const body: CreativeAssetFromTemplate = {
        sourceUrl: c.creativeTemplateSrc,
        height: c.height,
        width: c.width,
        fileType: c.creativeType.split("/")[0] as "image" | "video",
        name: c.name,
        size: c.size_in_bytes,
        duration: c.duration ?? undefined,
        mimetype: c.creativeType,
      };
      return body;
    });

    setSelectedAdGroupId(templateId);
    setSelectedAdGroup({ adGroup: adGroupToSet, creatives: creativeAssets });
  };

  const handleRowClick = async (templateId: string) => {
    const next = new Set(expandedIds);
    if (next.has(templateId)) {
      next.delete(templateId);
      setExpandedIds(next);
      return;
    }
    next.add(templateId);
    setExpandedIds(next);
    await fetchCreatives(templateId);
  };

  // Pre-select the ad group linked in the campaign template (runs once after list loads)
  useEffect(() => {
    if (hasAutoSelected.current || !preSelectedAdGroupId || filteredTemplates.length === 0) return;
    const match = filteredTemplates.find((t) => t.id === preSelectedAdGroupId);
    if (!match) return;
    hasAutoSelected.current = true;
    handleCheckboxChange(match.id, false);
  }, [filteredTemplates, preSelectedAdGroupId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- render states ----------

  if (!selectedAdvertiser) {
    return (
      <Card>
        <EmptyState>Select an advertiser above to view saved ad groups.</EmptyState>
      </Card>
    );
  }

  if (isPending) {
    return (
      <Card>
        <LoadingState>Loading ad groups…</LoadingState>
      </Card>
    );
  }

  if (filteredTemplates.length === 0) {
    return (
      <Card>
        <EmptyState>
          No saved {isVideoCampaign ? "video" : "display"} ad groups found for{" "}
          <strong>{selectedAdvertiser.name}</strong>.
          {panelMode && (
            <>
              {" "}
              <Link to="/ads-upload">Go to Upload Ad Groups →</Link>
            </>
          )}
        </EmptyState>
      </Card>
    );
  }

  return (
    <Card>
      <TableEl>
        <Thead>
          <tr>
            {panelMode && <Th style={{ width: 40 }} />}
            <Th>Name</Th>
            <Th>Created By</Th>
            <Th>Description</Th>
            <Th>Created Date</Th>
          </tr>
        </Thead>
        <tbody>
          {filteredTemplates.map((template) => {
            const isOpen = expandedIds.has(template.id);
            const creatives = creativesMap[template.id] ?? [];
            const isFetching = fetchingIds.has(template.id);
            const isSelected = selectedAdGroupId === template.id;

            return (
              <React.Fragment key={template.id}>
                <Tr>
                  {panelMode && (
                    <Td onClick={(e) => e.stopPropagation()}>
                      <CheckBox
                        label=""
                        checked={isSelected}
                        onChange={() => handleCheckboxChange(template.id, isSelected)}
                      />
                    </Td>
                  )}
                  <Td onClick={() => handleRowClick(template.id)}>
                    <ExpandIcon data-open={String(isOpen)}>▶</ExpandIcon>
                    {truncateText(template.name, 40)}
                  </Td>
                  <Td onClick={() => handleRowClick(template.id)}>{truncateText(template.createdBy, 36)}</Td>
                  <Td onClick={() => handleRowClick(template.id)}>{truncateText(template.description ?? "", 44)}</Td>
                  <Td onClick={() => handleRowClick(template.id)}>
                    {new Date(template.createdAt).toLocaleDateString()}
                  </Td>
                </Tr>

                {isOpen && (
                  <ExpandedTr>
                    <ExpandedTd colSpan={5}>
                      <CreativeAssets creativeAssets={creatives} isFetchingCreatives={isFetching} />
                    </ExpandedTd>
                  </ExpandedTr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </TableEl>
    </Card>
  );
};

export default SavedAdGroupsTab;
